import {yupResolver} from "@hookform/resolvers/yup";
import {
  Button,
  Stack,
  TextField,
  CircularProgress,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  useTheme
} from "@mui/material";
import {useForm, Controller} from "react-hook-form";
import * as yup from "yup";
import useApiRequest from "../../hooks/useApiRequest";
import {useState, useEffect, useCallback, useRef} from "react";
import {useSnackbar} from "notistack";
import AutocompleteField from "../ui/SearchBar/fields/AutocompleteField";

const schema = yup.object().shape({
  email: yup.string().required("El email es obligatorio").email("Email inválido"),
  ticket_type: yup.string().required("El tipo de ticket es obligatorio"),
  name: yup.string().required("El nombre es obligatorio"),
  surname: yup.string().optional(),
  internal_code: yup.string().when("ticket_type", {
    is: "registration",
    then: (s) => s.required("El código interno es obligatorio"),
    otherwise: (s) => s.optional()
  }),
  tenant_nif: yup.string().when("ticket_type", {
    is: "registration",
    then: (s) => s.required("El NIF de la empresa es obligatorio"),
    otherwise: (s) => s.optional()
  }),
  tenant_name: yup.string().when("ticket_type", {
    is: "registration",
    then: (s) => s.required("El nombre de la empresa es obligatorio"),
    otherwise: (s) => s.optional()
  }),
  tenant_id: yup.string().when("ticket_type", {
    is: "invitation",
    then: (s) => s.required("El ID del tenant es obligatorio").uuid("El ID del tenant debe ser un UUID válido"),
    otherwise: (s) => s.optional()
  }),
  product_id: yup.string().when("ticket_type", {
    is: "invitation",
    then: (s) => s.required("El producto es obligatorio"),
    otherwise: (s) => s.optional()
  })
});

const TicketForm = ({prefilled = {}, onSuccess = null}) => {
  const theme = useTheme();
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [productOptions, setProductOptions] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const latestTenantRequest = useRef(0);
  const {enqueueSnackbar} = useSnackbar();
  const {loading, create} = useApiRequest({
    baseUrl: `${process.env.REACT_APP_API_AUTH}/ts-bridge/generate-ticket`,
    resource: "tickets"
  });

  const {list: listTenants} = useApiRequest({
    baseUrl: `${process.env.REACT_APP_API_AUTH}/admin/tenants`,
    resource: "tenants"
  });

  const {read: getTenantProducts} = useApiRequest({
    baseUrl: `${process.env.REACT_APP_API_AUTH}/admin/tenants`,
    resource: "products"
  });

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: {errors},
    reset,
    setValue
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      email: prefilled.email || "",
      ticket_type: prefilled.ticket_type || "",
      internal_code: prefilled.internal_code || "",
      name: prefilled.name || "",
      surname: prefilled.surname || ""
    }
  });

  const hasPrefilledData = prefilled?.email && prefilled?.name && prefilled?.surname;

  useEffect(() => {
    if (hasPrefilledData) {
      setValue("email", prefilled.email);
      setValue("name", prefilled.name);
      setValue("surname", prefilled.surname);
      setValue("ticket_type", "invitation");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPrefilledData]);

  const ticketType = watch("ticket_type");

  // Opciones del campo tenant: se buscan por nombre y se excluye TS Support.
  const loadTenants = useCallback(
    async (term) => {
      const result = await listTenants(term ? {name: term} : {});
      const tenants = Array.isArray(result?.data?.data) ? result.data.data : [];
      return tenants
        .filter((tenant) => tenant.name !== "TS Support")
        .map((tenant) => ({id: tenant.id, label: `${tenant.name} (${tenant.nif})`}));
    },
    [listTenants]
  );

  const handleSave = async (data) => {
    const {email, ticket_type, name, surname, internal_code, tenant_nif, tenant_name, tenant_id, product_id} = data;

    const payload =
      ticket_type === "registration"
        ? {email, ticket_type, name, surname, internal_code, tenant_nif, tenant_name}
        : {email, ticket_type, name, surname, tenant_id, product_id};

    const result = await create(payload);
    if (!result?.success) return;

    reset();
    setSelectedTenantId(null);
    enqueueSnackbar("Ticket generado correctamente", {
      variant: "success",
      message: "Ticket generado correctamente"
    });
    if (onSuccess) onSuccess();
  };

  const handleSelectTenant = async (tenantId) => {
    const requestId = ++latestTenantRequest.current;
    setSelectedTenantId(tenantId || null);
    setProductOptions([]);
    setValue("product_id", "");
    if (!tenantId) {
      setLoadingProducts(false);
      return;
    }
    setLoadingProducts(true);
    const result = await getTenantProducts(`${tenantId}/products`);
    // Ignora respuestas de tenants anteriores si el usuario ha cambiado de tenant mientras tanto
    if (requestId !== latestTenantRequest.current) return;
    if (result?.success && Array.isArray(result.data?.data)) {
      setProductOptions(result.data.data);
    }
    setLoadingProducts(false);
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="200px">
        <CircularProgress />
        <Typography mt={2}>Generando ticket...</Typography>
      </Box>
    );
  }

  return (
    <Stack direction="column" spacing={2} minWidth={"400px"}>
      <TextField
        label="Email *"
        {...register("email")}
        error={!!errors.email}
        helperText={errors.email?.message}
        type="email"
        variant="outlined"
        disabled={hasPrefilledData}
        sx={{mb: 1}}
      />

      {!hasPrefilledData ? (
        <Controller
          name="ticket_type"
          control={control}
          render={({field}) => (
            <FormControl variant="outlined" error={!!errors.ticket_type}>
              <InputLabel>Tipo de Ticket *</InputLabel>
              <Select
                label="Tipo de Ticket *"
                {...field}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: theme.palette.colors.background,
                      color: theme.palette.text.primary
                    }
                  }
                }}
              >
                <MenuItem value="" disabled>
                  Selecciona un tipo
                </MenuItem>
                <MenuItem value="registration">Registration</MenuItem>
                <MenuItem value="invitation">Invitation</MenuItem>
              </Select>
              {errors.ticket_type && <FormHelperText>{errors.ticket_type?.message}</FormHelperText>}
            </FormControl>
          )}
        />
      ) : null}

      {(hasPrefilledData || ticketType) && (
        <>
          <TextField
            label="Nombre *"
            {...register("name")}
            error={!!errors.name}
            helperText={errors.name?.message}
            variant="outlined"
            disabled={hasPrefilledData}
          />

          <TextField
            label="Apellido"
            {...register("surname")}
            error={!!errors.surname}
            helperText={errors.surname?.message}
            variant="outlined"
            disabled={hasPrefilledData}
          />
        </>
      )}

      {ticketType === "registration" && (
        <>
          <TextField
            label="Código Interno *"
            {...register("internal_code")}
            error={!!errors.internal_code}
            helperText={errors.internal_code?.message}
            variant="outlined"
          />

          <TextField
            label="NIF Empresa *"
            {...register("tenant_nif")}
            error={!!errors.tenant_nif}
            helperText={errors.tenant_nif?.message}
            variant="outlined"
          />

          <TextField
            label="Nombre Empresa *"
            {...register("tenant_name")}
            error={!!errors.tenant_name}
            helperText={errors.tenant_name?.message}
            variant="outlined"
          />
        </>
      )}

      {(hasPrefilledData || ticketType === "invitation") && (
        <Controller
          name="tenant_id"
          control={control}
          render={({field}) => (
            <Stack>
              <AutocompleteField
                config={{
                  label: "Tenant *",
                  type: "autocomplete",
                  filterMode: "server",
                  width: "100%",
                  loadOptions: loadTenants
                }}
                value={field.value}
                size="medium"
                onChange={(tenantId) => {
                  field.onChange(tenantId);
                  handleSelectTenant(tenantId);
                }}
              />
              {errors.tenant_id && <FormHelperText error>{errors.tenant_id.message}</FormHelperText>}
            </Stack>
          )}
        />
      )}

      {(hasPrefilledData || ticketType === "invitation") && selectedTenantId && (
        <Controller
          name="product_id"
          control={control}
          render={({field}) => (
            <FormControl
              variant="outlined"
              error={!!errors.product_id}
              fullWidth
              disabled={loadingProducts || !selectedTenantId}
            >
              <InputLabel>Producto *</InputLabel>
              <Select
                label="Producto *"
                {...field}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: theme.palette.colors.background,
                      color: theme.palette.text.primary
                    }
                  }
                }}
              >
                <MenuItem value="" disabled>
                  {loadingProducts ? "Cargando productos..." : "Selecciona un producto"}
                </MenuItem>
                {productOptions
                  .filter((product) => product.product_name !== "Identity")
                  .map((product) => (
                    <MenuItem key={product.product_id} value={product.product_id}>
                      {product.product_name || product.product_id}
                    </MenuItem>
                  ))}
              </Select>
              {errors.product_id && <FormHelperText>{errors.product_id?.message}</FormHelperText>}
            </FormControl>
          )}
        />
      )}

      <Button variant="contained" color="primary" onClick={handleSubmit(handleSave)} disabled={loading}>
        Generar Ticket
      </Button>
    </Stack>
  );
};

export default TicketForm;
